import * as React from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Modal from "@mui/material/Modal";
import AddIcon from "@mui/icons-material/Add";
import {
  IconButton,
  Stack,
  TextField,
  Autocomplete,
  Checkbox,
} from "@mui/material";
import { Form, Formik, getIn, useFormikContext } from "formik";
import * as Yup from "yup";
import { Close } from "@mui/icons-material";
import {
  useAddProductMutation,
  useGetAllItemsQuery,
  usePartnersQuery,
  useUpdateProductMutation,
} from "../../Slices/roomNateApis";
import { ErrorAlert, SuccessAlert } from "../../utils/Alerts";
import { LoadingButton } from "@mui/lab";
import { useGlobalAuthContext } from "../../context/AuthContext";
import { useGlobalContext } from "../../context/GlobalContext";
import { SketchPicker } from "react-color";
import axios from "axios";
import { baseUrl } from "../../utils/baseUrl";

import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  height: "auto",
  maxHeight: "90%",
  overflowY: "auto",
  width: { xs: "90%", md: "650px" },
  transform: "translate(-50%, -50%)",
  bgcolor: "background.paper",
  border: "2px solid #000",
  boxShadow: 24,
  p: 4,
};

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

export default function BasicModal({ editProduct, setEditProduct }) {
  const { currentUserSessionStorage } = useGlobalAuthContext();
  const { activePartner } = useGlobalContext();
  const { data: partnersData } = usePartnersQuery("", {
    skip: !currentUserSessionStorage,
  });

  const [formValue, setFormValue] = React.useState(null)
  
    const FormikWatcher = ({ onValuesChange }) => {
      const { values } = useFormikContext();
    
      React.useEffect(() => {
        onValuesChange(values);
      }, [values, onValuesChange]);
    
      return null;
    };

  const { data: FetchItems } = useGetAllItemsQuery({itemType:1 , activePartner: formValue?.partners});

  const { data: FetchAllItems } = useGetAllItemsQuery({itemType:1});


  const filteredItems = FetchAllItems?.items?.filter(item => editProduct?.items?.includes(item._id));


  const formRef = React.useRef();
  const fieldRefs = React.useRef({});

  const [addProduct, resultStats] = useAddProductMutation();
  const [updateProduct, updateResultStats] = useUpdateProductMutation();

  const [open, setOpen] = React.useState(false);
  const [load, setLoad] = React.useState(false);
  const [successAlert, setSuccessAlert] = React.useState(false);
  const [errorAlert, setErrorAlert] = React.useState(false);
  const [showColorPicker, setShowColorPicker] = React.useState(null);

  const colorPickerRef = React.useRef(null);

  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        colorPickerRef.current &&
        !colorPickerRef.current.contains(event.target)
      )
        setShowColorPicker(false);
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleOpen = React.useCallback(() => setOpen(true), []);
  const handleClose = React.useCallback(() => setOpen(false), []);

  const validationSchema = Yup.object().shape({
    name: Yup.string().required("Name is required"),
  });

  React.useEffect(() => {
    if (resultStats?.isLoading) {
      setLoad(true);
    }

    if (!resultStats?.isLoading) {
      setLoad(false);
    }

    if (resultStats?.isSuccess) {
      setSuccessAlert(true);
      setTimeout(() => {
        setSuccessAlert(false);
      }, 2000);
      handleClose();
    }

    if (resultStats?.isError) {
      setErrorAlert(true);
      setTimeout(() => {
        setErrorAlert(false);
      }, 2000);
    }
  }, [resultStats, handleClose]);

  React.useEffect(() => {
    if (updateResultStats?.isLoading) {
      setLoad(true);
    }

    if (!updateResultStats?.isLoading) {
      setLoad(false);
    }

    if (updateResultStats?.isSuccess) {
      setSuccessAlert(true);
      setTimeout(() => {
        setSuccessAlert(false);
      }, 2000);
      handleClose();
      setEditProduct({});
    }

    if (updateResultStats?.isError) {
      setErrorAlert(true);
      setTimeout(() => {
        setErrorAlert(false);
      }, 2000);
    }
  }, [updateResultStats, handleClose, setEditProduct]);

  const scrollToError = (errors) => {
    const firstErrorKey = Object.keys(errors)[0];

    if (firstErrorKey !== "variants") {
      if (fieldRefs.current[firstErrorKey]) {
        fieldRefs.current[firstErrorKey].scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }
    } else {
      const firstVariantIndex = Object.keys(errors.variants)[0];

      if (firstVariantIndex !== undefined) {
        const firstFieldKey = Object.keys(
          errors.variants[firstVariantIndex]
        )[0];
        const refKey = `variants.[${firstVariantIndex}].${firstFieldKey}`;

        if (fieldRefs.current[refKey]) {
          fieldRefs.current[refKey].scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
        }
      }
    }
  };

  // Track previous open state to detect modal close transition
  const prevOpenRef = React.useRef(open);
  React.useEffect(() => {
    // Only clear editProduct when modal actually CLOSES (was open, now closed)
    if (prevOpenRef.current && !open && editProduct?._id) {
      setEditProduct({});
    }
    prevOpenRef.current = open;
  }, [open, editProduct?._id, setEditProduct]);

  React.useEffect(() => {
    if (editProduct?._id) handleOpen();
  }, [editProduct?._id, handleOpen]);

  return (
    <>
      {successAlert && (
        <SuccessAlert title="SKU has been created successfully!" />
      )}

      {errorAlert && <ErrorAlert title="Something Went Wrong!" />}

      <Button onClick={handleOpen} variant="outlined" endIcon={<AddIcon />}>
        Add SKU
      </Button>

      <Modal
        open={open}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box sx={style}>
          <Box position="fixed" top={10} right={0}>
            <IconButton onClick={handleClose}>
              <Close />
            </IconButton>
          </Box>
          <Formik
            innerRef={formRef}
            enableReinitialize={true}
            initialValues={{
              name: editProduct?.name || "",
              price: editProduct?.price || "",
              description: editProduct?.description || "",
              partners: (editProduct && Object.keys(editProduct).length > 0) ? (editProduct.partners || null) : activePartner ? [activePartner] : null,
              colors: editProduct?.colors || [],
              url: editProduct?.url || "",
              items: filteredItems || [],
              image: editProduct?.image || null,
              imageId: editProduct?.imageId || null,
            }}
            validationSchema={validationSchema}
            onSubmit={(values, { setSubmitting }) => {
              setSubmitting(false);
              let data;
              const {
                name,
                price,
                description,
                colors,
                imageId,
                url,
                items,
                partners,
              } = values;

              data = {
                name,
                price,
                description,
                colors,
                image: imageId,
                url,
                items,
                partners,
              };

              if (editProduct?._id)
                updateProduct({ id: editProduct?._id, data });
              else addProduct(data);
            }}
          >
            {({
              values,
              touched,
              errors,
              handleChange,
              handleBlur,
              setValues,
              setFieldValue,
              validateForm,
            }) => (
              <Form noValidate autoComplete="off">
                <FormikWatcher onValuesChange={(vals) => setFormValue(vals)} />

                <Typography
                  id="modal-modal-title"
                  fontWeight={"bold"}
                  fontSize={"20px"}
                  variant="h6"
                  mb={2}
                >
                  {editProduct?._id ? "Update" : "Add"} SKU
                </Typography>

                <br></br>

                <Stack
                  ref={(el) => (fieldRefs.current["image"] = el)}
                  onClick={() =>
                    document.getElementById("product-image").click()
                  }
                >
                  {Boolean(
                    getIn(touched, "image") && getIn(errors, "image")
                  ) && <span className="error">{errors?.image}</span>}

                  <input
                    id="product-image"
                    hidden
                    accept="image/*"
                    name="image"
                    type="file"
                    onChange={async (e) => {
                      const file = e.target.files[0];

                      const formData = new FormData();
                      formData.append("file", file);
                      const response = await axios.post(
                        `${baseUrl}/g/fileUpload`,
                        formData,
                        {
                          headers: {
                            Authorization: currentUserSessionStorage,
                            "Content-Type": "multipart/form-data",
                          },
                        }
                      );

                      setFieldValue("image", response?.data?.url);
                      setFieldValue("imageId", response?.data?.id);
                    }}
                  />

                  <Box>
                    <img
                      src={
                        values?.image
                          ? values?.image
                          : "https://placehold.jp/90x90.png?text=Product"
                      }
                      alt={values?.image ? values?.image : "placeHolder"}
                      height="100px"
                    />
                  </Box>
                  <Typography fontWeight={"bold"} mb={1}>
                    Image
                  </Typography>
                </Stack>

                <Stack my={2}>
                  <TextField
                    id="outlined-basic"
                    label="Name"
                    variants="outlined"
                    name="name"
                    fullWidth
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.name}
                    required
                    ref={(el) => (fieldRefs.current["name"] = el)}
                    helperText={
                      getIn(touched, "name") && getIn(errors, "name")
                        ? getIn(errors, "name")
                        : ""
                    }
                    error={Boolean(
                      getIn(touched, "name") && getIn(errors, "name")
                    )}
                  />
                </Stack>
                <Stack my={2}>
                  <TextField
                    id="outlined-basic"
                    label="Price"
                    variants="outlined"
                    name="price"
                    fullWidth
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.price}
                    required
                    ref={(el) => (fieldRefs.current["price"] = el)}
                    helperText={
                      getIn(touched, "price") && getIn(errors, "price")
                        ? getIn(errors, "price")
                        : ""
                    }
                    error={Boolean(
                      getIn(touched, "price") && getIn(errors, "price")
                    )}
                  />
                </Stack>

                <Stack my={2}>
                  <TextField
                    id="outlined-basic"
                    label="Description"
                    variants="outlined"
                    name="description"
                    fullWidth
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.description}
                    required
                    ref={(el) => (fieldRefs.current["description"] = el)}
                    helperText={
                      getIn(touched, "description") &&
                      getIn(errors, "description")
                        ? getIn(errors, "description")
                        : ""
                    }
                    error={Boolean(
                      getIn(touched, "description") &&
                        getIn(errors, "description")
                    )}
                  />
                </Stack>

                <Stack my={2}>
                  <TextField
                    id="outlined-basic"
                    label="Destination URL"
                    variants="outlined"
                    name="url"
                    fullWidth
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.url}
                    required
                    ref={(el) => (fieldRefs.current["url"] = el)}
                    helperText={
                      getIn(touched, "url") && getIn(errors, "url")
                        ? getIn(errors, "url")
                        : ""
                    }
                    error={Boolean(
                      getIn(touched, "url") && getIn(errors, "url")
                    )}
                  />
                </Stack>

                <Stack mt={2}>
                  <Autocomplete
                    multiple
                    id="checkboxes-partners-demo"
                    options={
                      partnersData?.map((partner) => ({
                        label: partner?.title || "",
                        value: partner?._id || "",
                      })) || []
                    }
                    getOptionLabel={(option) => option.label}
                  value={values?.partners?.map(id => {
                    const item = partnersData?.find(obj => obj._id === id);
                    return item ? { label: item.title, value: item._id } : null;
                  }).filter(item => item !== null) || []}
                    onChange={(_, value) =>
                      setValues({
                        ...values,
                        partners: value.map((val) => val.value),
                      })
                    }
                    isOptionEqualToValue={(option, value) => option.value === value.value}
                    renderOption={(props, option, { selected }) => (
                      <li {...props} key={option.value}>
                        <Checkbox
                          icon={icon}
                          checkedIcon={checkedIcon}
                          style={{ marginRight: 8 }}
                          checked={selected}
                        />
                        {option.label}
                      </li>
                    )}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        name="partners"
                        label="Partner"
                        placeholder="partners"
                        onBlur={handleBlur}
                        helperText={
                          getIn(touched, "partners") && getIn(errors, "partners")
                            ? getIn(errors, "partners")
                            : ""
                        }
                        error={Boolean(
                          getIn(touched, "partners") && getIn(errors, "partners")
                        )}
                      />
                    )}
                  />
                </Stack>

                {FetchItems?.items && (
                  <Stack mt={2}>
                    <Autocomplete
                      onChange={(_, value) =>
                        setValues({
                          ...values,
                          items: value.map((item) => ({ ...item, qty: 1 })),
                        })
                      }
                      required
                      name="items"
                      multiple
                      id="checkboxes-tags-demo"
                      isOptionEqualToValue={(option, value) => option._id === value._id}
                      value={values?.items}
                      options={FetchItems?.items}
                      getOptionLabel={(option) => option.name}
                      renderOption={(props, option, { selected }) => (
                        <li {...props} key={option._id}>
                          <Checkbox
                            icon={icon}
                            checkedIcon={checkedIcon}
                            style={{ marginRight: 8 }}
                            checked={selected}
                          />
                          {option.name}
                        </li>
                      )}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          name="items"
                          label="Items"
                          placeholder="Items"
                          onBlur={handleBlur}
                          helperText={
                            getIn(touched, "items") && getIn(errors, "items")
                              ? getIn(errors, "items")
                              : ""
                          }
                          error={Boolean(
                            getIn(touched, "items") && getIn(errors, "items")
                          )}
                        />
                      )}
                    />
                  </Stack>
                )}

                <Stack mt={2} direction="row" alignItems="center">
                  <label htmlFor="primaryColor" style={{ marginRight: "10px" }}>
                    Product Colors:
                  </label>

                  {values?.colors.map((color, index) => (
                      <Box key={index} sx={{ position: 'relative', display: 'inline-block', marginRight: '10px' }}>
                        <Box
                          onClick={() => setShowColorPicker(index + 1)}
                          sx={{
                            width: "50px",
                            height: "50px",
                            backgroundColor: color,
                            border: "1px solid #ccc",
                            cursor: "pointer",
                          }}
                        />
                        <Box
                          onClick={() => {
                            const updatedColors = [...values.colors];
                            updatedColors.splice(index, 1);
                            setFieldValue("colors", updatedColors);
                          }} 
                          sx={{
                            position: 'absolute',
                            top: '-5px',
                            right: '-5px',
                            backgroundColor: '#fff',
                            border: '1px solid #ccc',
                            borderRadius: '50%',
                            width: '20px',
                            height: '20px',
                            fontSize: '14px',
                            textAlign: 'center',
                            lineHeight: '20px',
                            cursor: 'pointer',
                            zIndex: 1,
                          }}
                        >
                          ×
                        </Box>
                      </Box>
                    ))}

                  <Box
                    onClick={() =>
                      setShowColorPicker(values?.colors?.length + 1)
                    }
                    sx={{
                      width: "50px",
                      height: "50px",
                      display: "flex",
                      backgroundColor: null,
                      border: "1px solid #ccc",
                      cursor: "pointer",
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                  >
                    +
                  </Box>
                </Stack>

                {showColorPicker && (
                  <Box
                    ref={colorPickerRef}
                    sx={{
                      position: "absolute",
                      bottom: 60,
                      left: 230,
                      zIndex: 1,
                    }}
                  >
                    <SketchPicker
                      color={values?.colors?.[showColorPicker]}
                      onChange={(color) => {
                        const index = showColorPicker - 1;
                        const updatedColors = [...values.colors];

                        if (updatedColors[index]) {
                          updatedColors[index] = color.hex;
                        } else {
                          updatedColors.push(color.hex);
                        }

                        setFieldValue("colors", updatedColors);
                      }}
                    />
                  </Box>
                )}

                <Stack mt={2}>
                  <LoadingButton
                    loading={load}
                    variant="contained"
                    fullWidth
                    type="submit"
                    onClick={() => {
                      validateForm().then((errors) => {
                        if (Object.keys(errors).length > 0) {
                          scrollToError(errors);
                        }
                      });
                    }}
                  >
                    {editProduct?._id ? "Update" : "Add"}
                  </LoadingButton>
                </Stack>
              </Form>
            )}
          </Formik>
        </Box>
      </Modal>
    </>
  );
}
